import { useQuery } from "@tanstack/react-query";

import { apiGet, mockFor, mockResponse, queryKey } from "./client";
import {
  ASSET_TYPES,
  GUARANTOR_BANDS,
  RISK_TIERS,
  TICKET_BUCKETS,
  type AssetType,
  type AutomationReadinessMatrix,
  type GuarantorBand,
  type ReadinessCell,
  type ReadinessCondition,
  type ReadinessThresholds,
  type RiskTier,
  type TicketBucket,
} from "./types";

export const DEFAULT_THRESHOLDS: ReadinessThresholds = {
  maxExceptionRatePct: 8,
  maxOverrideRatePct: 5,
  maxMedianDecisionHours: 6,
  minVolume: 40,
};

/** Bigger ticket → slower, messier. */
const BUCKET_WEIGHT: Record<TicketBucket, number> = {
  "0-10K": 0,
  "10-25K": 0.6,
  "25-50K": 1.2,
  "50-100K": 1.9,
  "100-150K": 2.6,
  "150-200K": 3.2,
  "200-250K": 3.9,
  "Over 250K": 4.7,
};

/** Volume thins out sharply at the top of the ticket range. */
const BUCKET_VOLUME: Record<TicketBucket, number> = {
  "0-10K": 420,
  "10-25K": 330,
  "25-50K": 210,
  "50-100K": 120,
  "100-150K": 58,
  "150-200K": 26,
  "200-250K": 12,
  "Over 250K": 8,
};

// "Unknown" is what the live backend reports for both of these axes — no guarantor count and no
// asset type exist in athena_ue_performance. Weighted neutrally here so the mock, which is only
// used when the endpoint is switched off, does not imply a risk profile for an unknown value.
const GUARANTOR_WEIGHT: Record<GuarantorBand, number> = {
  "0": 0, "1": 0.7, "2+": 1.6, Unknown: 0.8,
};
const GUARANTOR_VOLUME: Record<GuarantorBand, number> = {
  "0": 0.5, "1": 0.34, "2+": 0.16, Unknown: 0,
};
const TIER_WEIGHT: Record<RiskTier, number> = { A: 0, B1: 0.9, B2: 1.8, B3: 2.9, Other: 1.5 };
const TIER_VOLUME: Record<RiskTier, number> = {
  A: 0.42, B1: 0.29, B2: 0.19, B3: 0.1, Other: 0,
};
const ASSET_WEIGHT: Record<AssetType, number> = {
  "Titled Vehicles": 0,
  "IT & Office Equipment": 0.4,
  "Material Handling": 0.9,
  "Construction Equipment": 1.4,
  "Medical Equipment": 2.1,
  Unknown: 1.0,
};
const ASSET_VOLUME: Record<AssetType, number> = {
  "Titled Vehicles": 0.3,
  "Construction Equipment": 0.24,
  "Material Handling": 0.2,
  "IT & Office Equipment": 0.16,
  "Medical Equipment": 0.1,
  Unknown: 0,
};

export function evaluateCell(
  cell: Omit<ReadinessCell, "failingConditions">,
  thresholds: ReadinessThresholds,
): ReadinessCondition[] {
  const failing: ReadinessCondition[] = [];
  if (cell.exceptionRatePct > thresholds.maxExceptionRatePct) failing.push("exception_rate");
  if (cell.overrideRatePct > thresholds.maxOverrideRatePct) failing.push("override_rate");
  if (cell.medianDecisionHours > thresholds.maxMedianDecisionHours) failing.push("decision_time");
  if (cell.applicationsDecided < thresholds.minVolume) failing.push("volume");
  return failing;
}

function generateMatrix(): AutomationReadinessMatrix {
  const cells: ReadinessCell[] = [];

  TICKET_BUCKETS.forEach((ticketBucket, bi) => {
    GUARANTOR_BANDS.forEach((guarantorBand, gi) => {
      RISK_TIERS.forEach((riskTier, ti) => {
        ASSET_TYPES.forEach((assetType, ai) => {
          const w =
            BUCKET_WEIGHT[ticketBucket] +
            GUARANTOR_WEIGHT[guarantorBand] +
            TIER_WEIGHT[riskTier] +
            ASSET_WEIGHT[assetType];
          // Deterministic wobble so the gradient reads as data, not a formula.
          const wobble = Math.sin(bi * 1.7 + gi * 2.3 + ti * 0.9 + ai * 1.3);

          const applicationsDecided = Math.max(
            3,
            Math.round(
              BUCKET_VOLUME[ticketBucket] *
                GUARANTOR_VOLUME[guarantorBand] *
                TIER_VOLUME[riskTier] *
                ASSET_VOLUME[assetType] *
                (1 + wobble * 0.18) *
                6,
            ),
          );

          const base = {
            ticketBucket,
            guarantorBand,
            riskTier,
            assetType,
            applicationsDecided,
            medianDecisionHours: Number(Math.max(1.1, 2.4 + w * 1.35 + wobble * 0.5).toFixed(1)),
            exceptionRatePct: Number(Math.max(0.4, 2.1 + w * 1.55 + wobble * 0.7).toFixed(1)),
            overrideRatePct: Number(Math.max(0.2, 1.2 + w * 1.05 + wobble * 0.5).toFixed(1)),
          };

          cells.push({ ...base, failingConditions: evaluateCell(base, DEFAULT_THRESHOLDS) });
        });
      });
    });
  });

  return { defaultThresholds: DEFAULT_THRESHOLDS, cells };
}

export async function fetchAutomationReadiness(
  params: { range?: string; office?: string } = {},
): Promise<AutomationReadinessMatrix> {
  if (mockFor("/automation-readiness")) return mockResponse(generateMatrix());
  return apiGet<AutomationReadinessMatrix>("/automation-readiness", {
    range: params.range,
    office: params.office,
  });
}

export function useAutomationReadiness(params: { range?: string; office?: string } = {}) {
  return useQuery({
    queryKey: queryKey("automation-readiness", params),
    queryFn: () => fetchAutomationReadiness(params),
    staleTime: 60 * 1000,
  });
}
